import {Schema} from '@zaiusinc/app-forms-schema';
import {AppInstallation} from '../types/AppInstallation';
import {AppLogEntry} from '../types/AppLogEntry';
import {AppVersion} from '../types/AppVersion';
import {versionPath} from './appVersion';
import {ZipApiError} from './ZipApiError';
import {ZipApiTimeoutError} from './ZipApiTimeoutError';
import {showToasts} from './toaster';
import {Intent} from '@blueprintjs/core';
import { CGBaseTypeDefinition, CGContentType, CGProductInstance, CGSourceMetadata, DataSyncItem, ObjectDefinition, OptimizelyProduct, ProductInstance, ProductInstances } from '../types/DataSyncItem';
import { convertSchemaV2ToObjectDefinition } from './convertV2Schema';
import { DataSyncExecution } from '../types/DataSyncExecution';
import { CGLanguage, sourceMetadataLocales } from '../types/ContentGraph';
import { Sort } from '../types/Sort';
import { GetCGDestinationSchemaResponse, GetSchemaResponse, GetSourceSchemaResponse, ListDestinationSchemasResponse, ListSourceSchemasResponse, Schema as SourceSchema } from '../types/Schema';
import { directoryStore } from '../stores/DirectoryStore';
import { OpalToolDescriptionsResponse, UpdateOpalToolDescriptionsRequest } from '../types/OpalToolDescriptions';
import { GetDataSyncMetricsResponse } from '../types/DataSyncMetrics';
import { MetricsGranularity } from './metricsUtils';

const ZIP_API_URI: string = import.meta.env.VITE_ZIP_API_URI || (() => {
  const url = new URL(window.location.href);
  const stem = url.host.split('.').slice(1);
  if (stem[stem.length - 2] === 'zaius') {
    // Legacy quirks.
    stem.unshift('directory');
  }
  url.host = ['api'].concat(stem).join('.');
  return url.origin;
})();

/**
 * Escapes a value for use as a single URL path segment. Without this a value
 * containing `/` or `%` silently reroutes the request to a different endpoint.
 */
const seg = (value: string | number): string => encodeURIComponent(String(value));

const AUTH_HEADERS: HeadersInit = import.meta.env.VITE_PRISM_ACCESS_TOKEN ? { 'authorization': `Bearer ${import.meta.env.VITE_PRISM_ACCESS_TOKEN}` } : {};

const PR_LANE_HEADERS: HeadersInit = import.meta.env.VITE_PR_LANE_ENABLED
  ? (() => {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)ocp-pr-lane=([^;]+)/);
      const lane = cookieMatch?.[1]
        || new URLSearchParams(window.location.search).get('ocp-pr-lane');
      if (lane) {
        console.log('[PR Lane] Activating lane:', lane);
      }
      return lane ? { 'ocp-pr-lane': lane } : {} as HeadersInit;
    })()
  : {};

function logPrLaneResponseHeaders(response: Response) {
  if (!import.meta.env.VITE_PR_LANE_ENABLED) return;
  response.headers.forEach((value, key) => {
    if (key.startsWith('ocp-pr-lane')) {
      console.log(`[PR Lane] Response ${key}: ${value}`);
    }
  });
}

export type ToastIntent = 'info' | 'success' | 'warning' | 'danger';

export type EnvelopedResponse<K extends string, T> = {
  [key in K]: T;
};

export interface AppListingResult extends AppVersion {
  scoped_installations?: Array<{scope: string}>;
}

export interface ListAppsV2Response {
  results: AppListingResult[];
}

export interface ListAppInstallationsResponse {
  installations?: AppInstallation[];
}

export interface FetchAppResponse {
  app: AppVersion;
}

export interface FetchAppVersionResponse {
  app_version: AppVersion;
}

export interface InstallResponse {
  app_installation: AppInstallation;
}

export interface FetchFormDataResponse {
  form_data_json: Schema.FormData;
}

export interface SubmitFormDataResponse {
  form_data_json: Schema.FormData;
  response_json: {
    redirect?: string;
    redirectMode?: 'url' | 'settings';
    errors: {[field: string]: string[]};
    toasts: Array<{intent: ToastIntent, message: string}>;
  };
}

export interface ListFunctionsResponse {
  function_endpoints: {
    [name: string]: string;
  };
}

export interface AuthorizationRequestResponse {
  form_data_json: Schema.FormData;
  response_json: {
    redirect?: string;
    redirectMode?: 'url' | 'settings';
    errors: {[field: string]: string[]};
    toasts: Array<{intent: ToastIntent, message: string}>;
  };
  auth_request_id: string;
}

export interface CanUninstallResponse {
  message?: string;
  uninstallable: boolean;
}

export interface ListAppLogEntriesResponse {
  log_entry: AppLogEntry[];
  cursor_token: string;
}

export interface DataSyncResponse {
  dataSync: DataSyncItem;
}

export interface ListDataSyncsResponse {
  dataSyncItems: DataSyncItem[];
  totalCount?: number;
}

export interface ListDataSyncExecutionResponse {
  executions: DataSyncExecution[];
}

export interface CreateCGSchemaRequest {
  content_type: CGContentType;
  preset: string;
  instance_id: string;
  tracker_id: string;
  // Graph source the new content type is created on; empty falls back to `ocp` on the backend.
  graph_source_id?: string;
}

export interface SourceJob {
  name: string;
  description: string;
}

export interface ListSourcesResponse {
  name: string;
  jobs: SourceJob[];
}

export enum OpalToolStatus {
  REGISTERED = 'REGISTERED',
  NOT_REGISTERED = 'NOT_REGISTERED',
  REGISTERED_BY_ANOTHER = 'REGISTERED_BY_ANOTHER'
}

export interface OpalTool {
  id: string;
  name: string;
  description: string;
  status: OpalToolStatus;
  discovery_url: string;
}

export interface ListOpalToolResponse {
  instance_id: string;
  tools: OpalTool[];
}

export interface RegisterOpalToolResponse {
  tool: OpalTool;
}

export interface OpalInstance {
  InstanceId: string;
  InstanceName: string;
  ProductName: string;
  OrganizationId: string;
  Region: string;
}

// User Management reports Opal instances under either product name
const OPAL_PRODUCT_NAMES = ['Opal', 'Mark'];

export interface ListOpalInstancesResponse {
  totalItemCount: number;
  items: OpalInstance[];
}

// V1 response — instances are ProductInstance (id + name only)
export interface CMSUIExtensionProject {
  project_name: string;
  instances: ProductInstance[];
}

export interface ListCMSUIExtensionProjectsResponse {
  projects: CMSUIExtensionProject[];
}

// V2 response — instances include managed_by_different_account
export interface CMSUIExtensionInstanceV2 {
  id: string;
  name: string;
  managed_by_different_account: boolean;
}

export interface CMSUIExtensionProjectV2 {
  project_name: string;
  instances: CMSUIExtensionInstanceV2[];
}

export interface ListCMSUIExtensionProjectsV2Response {
  projects: CMSUIExtensionProjectV2[];
}

export interface CmsProjectWithInstances {
  projectName: string;
  instances: CMSUIExtensionInstanceV2[];
}

export interface DestinationSchemaQueryOpts {
  dataSyncId?: string;
  sourceContext?: {
    sourceProduct: OptimizelyProduct;
    sourceProductInstanceId: string;
  };
}

export function buildDestinationSchemaQuery(opts?: DestinationSchemaQueryOpts): string {
  if (!opts) {
    return '';
  }
  const params = new URLSearchParams();
  // EDIT wins over CREATE; data-syncs side enforces the same precedence.
  if (opts.dataSyncId) {
    params.append('data_sync_id', opts.dataSyncId);
  } else if (opts.sourceContext) {
    params.append('source_product', opts.sourceContext.sourceProduct);
    params.append('source_product_instance_id', opts.sourceContext.sourceProductInstanceId);
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

export abstract class ZipApi {

  public static async listAppsV2(trackerId: string): Promise<ListAppsV2Response> {
    const response = await this.performGet(`${ZIP_API_URI}/v2/apps?tracker_id=${seg(trackerId)}`);
    return await response.json();
  }

  public static async listAppInstallations(trackerId: string): Promise<ListAppInstallationsResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/installs`);
    return await response.json();
  }

  public static async listAppInstalls(trackerId: string, appFeature: string): Promise<ListAppInstallationsResponse> {
    const queryString = new URLSearchParams({includes_features: appFeature}).toString();
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/installs?${queryString}`);
    return await response.json();
  }

  /** With a tracker, an app that is not PUBLIC comes at the version its organisation's grant resolves to. */
  public static async fetchApp(appId: string, trackerId?: string): Promise<FetchAppResponse> {
    const query = trackerId ? `?tracker_id=${seg(trackerId)}` : '';
    const response = await this.performGet(`${ZIP_API_URI}/v1/apps/${seg(appId)}${query}`);
    return await response.json();
  }

  public static async fetchAppVersion(appId: string, version: string): Promise<FetchAppVersionResponse> {
    // versionPath, not seg: a preview version is "{track}/{sha}" and the route captures it as a
    // multi-segment wildcard, so its separators must stay separators.
    const response = await this.performGet(`${ZIP_API_URI}/v1/apps/${seg(appId)}/versions/${versionPath(version)}`);
    return await response.json();
  }

  /**
   * The version a new install by the account gets, or null when there is none: the latest RUNNING version whose
   * semver carries no pre-release tag, or for an app that is not PUBLIC the version its organisation's grant
   * resolves to.
   */
  public static async fetchLatestRunningAppVersion(appId: string, trackerId: string): Promise<string | null> {
    try {
      const response = await this.performGet(
        `${ZIP_API_URI}/v1/apps/${seg(appId)}/versions/latest-running?tracker_id=${seg(trackerId)}`
      );
      const body = await response.json() as FetchAppVersionResponse;
      return body.app_version?.id?.version ?? null;
    } catch {
      return null;
    }
  }

  public static async install(trackerId: string, appId: string, version?: string, scope?: string): Promise<InstallResponse> {
    const base = `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/installs`;
    const url = scope ? `${base}?scope=${seg(scope)}` : base;
    const response = await this.performPost(url, JSON.stringify({app_id: appId, version}));
    return await response.json();
  }

  public static async canUninstall(installId: number): Promise<CanUninstallResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${seg(installId)}/canUninstall`);
    return await response.json();
  }

  public static async uninstall(trackerId: string, appId: string, scope?: string): Promise<InstallResponse> {
    const url = scope
      ? `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/installs/${seg(appId)}?scope=${seg(scope)}`
      : `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/installs/${seg(appId)}`;
    const response = await this.performRequest('DELETE', url);
    return await response.json();
  }

  public static async createDataSync(dataSync: DataSyncItem): Promise<DataSyncResponse> {
    // use nestes_mappings for new data syncs
    const response = await this.performPost(`${ZIP_API_URI}/v1/data-syncs`, JSON.stringify(dataSync));
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async updateDataSync(dataSync: DataSyncItem): Promise<DataSyncResponse> {
    const response = await this.performRequest(
      'PUT',
      `${ZIP_API_URI}/v1/data-syncs/${seg(dataSync.id)}`,
      JSON.stringify(dataSync)
    );
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async getDataSync(dataSyncId: string): Promise<DataSyncResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}`);
    const result = await response.json();
    if (result.dataSync) {
      result.dataSync = new DataSyncItem(result.dataSync);
    }
    return result;
  }

  public static async listDataSyncs(
    offset: number,
    limit: number,
    sort?: Sort,
    trackerId?: string
  ): Promise<ListDataSyncsResponse> {
    const params = new URLSearchParams({
      'offset': offset.toString(),
      'limit': limit.toString(),
    });

    if (sort) {
      params.append('sort.field', sort.field);
      params.append('sort.direction', sort.direction.toString());
    }

    if (trackerId) {
      params.append('tracker_id', trackerId);
    }

    const queryString = params.toString();
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs?${queryString}`);
    return await response.json();
  }

  public static async getDataSyncMetrics(
    trackerId: string,
    datasyncIds: string[],
    fromEpoch: number,
    toEpoch: number,
    granularity: MetricsGranularity
  ): Promise<GetDataSyncMetricsResponse> {
    const params = new URLSearchParams();
    params.append('tracker_id', trackerId);
    params.append('from_epoch', fromEpoch.toString());
    params.append('to_epoch', toEpoch.toString());
    params.append('granularity', granularity);
    datasyncIds.forEach((id) => params.append('datasync_ids', id));

    const response = await this.performGet(`${ZIP_API_URI}/v1/metrics/data-syncs?${params.toString()}`);
    return await response.json();
  }

  public static async enableDataSync(dataSyncId: string) {
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}/enable`);
    return response.ok;
  }

  public static async disableDataSync(dataSyncId: string) {
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}/disable`);
    return response.ok;
  }

  public static async deleteDataSync(dataSyncId: string) {
    const response = await this.performRequest('DELETE', `${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}`);
    return response.ok;
  }

  public static async listDataSyncExecutions(dataSyncId: string, offset: number, limit: number):
    Promise<ListDataSyncExecutionResponse> {
    const params = new URLSearchParams({
      'offset': offset.toString(),
      'limit': limit.toString(),
      'sort.field': 'createdAt',
      'sort.direction': '1',
    });

    const queryString = params.toString();
    const response = await this.performGet(`${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}/executions?${queryString}`);
    return await response.json();
  }

  public static async listProductInstances(trackerId: string, products?: OptimizelyProduct):
  Promise<ProductInstances[]> {
    const queryParams = products ? `?${new URLSearchParams({products})}` : '';
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/product_instances${queryParams}`);
    const body =  await response.json() as EnvelopedResponse<'instances', ProductInstances[]>;
    if (body.instances) {
      return body.instances;
    } else {
      return [];
    }
  }

  public static async listOpalInstances(organizationId: string, userEmail: string): Promise<OpalInstance[]> {
    const url = `${import.meta.env.VITE_USER_MANAGEMENT_URI}/api/users/${userEmail}/instancepermissions?size=1000`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${directoryStore.getOptiAccessToken()}`
    };

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ZipApiError(response);
    }

    const body = await response.json() as ListOpalInstancesResponse;

    if (body.items) {
      return body.items.filter((instance) =>
        instance.OrganizationId === organizationId && OPAL_PRODUCT_NAMES.includes(instance.ProductName));
    } else {
      return [];
    }
  }

  public static async listCMSUIExtensionInstances(trackerId: string): Promise<ProductInstance[]> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/cms-ui-extension-projects`
    );
    const body = await response.json() as ListCMSUIExtensionProjectsResponse;

    // Flatten projects into a single list of instances
    const instances: ProductInstance[] = [];
    if (body.projects) {
      body.projects.forEach((project) => {
        instances.push(...project.instances);
      });
    }

    // Sort instances by name
    return instances.sort((a, b) => a.name.localeCompare(b.name));
  }

  public static async listCMSUIExtensionProjectsV2(trackerId: string): Promise<CmsProjectWithInstances[]> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v2/accounts/${seg(trackerId)}/cms-ui-extension-projects`
    );
    const body = await response.json() as ListCMSUIExtensionProjectsV2Response;
    return (body.projects || []).map((p) => ({
      projectName: p.project_name,
      instances: p.instances,
    }));
  }

  public static async listCMSUIExtensionEnabledInstances(
    installationId: number
  ): Promise<string[]> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/installations/${seg(installationId)}/cms/extensions/enabled-instances`
    );
    const body = await response.json() as { enabled_cms_instance_ids: string[] };
    return body.enabled_cms_instance_ids || [];
  }

  public static async enableCMSUIExtension(
    installationId: number,
    cmsInstanceId: string
  ): Promise<void> {
    await this.performPost(
      `${ZIP_API_URI}/v1/installations/${seg(installationId)}/cms/${seg(cmsInstanceId)}/extensions/enable`,
      JSON.stringify({})
    );
  }

  public static async disableCMSUIExtension(
    installationId: number,
    cmsInstanceId: string
  ): Promise<void> {
    await this.performPost(
      `${ZIP_API_URI}/v1/installations/${seg(installationId)}/cms/${seg(cmsInstanceId)}/extensions/disable`,
      JSON.stringify({})
    );
  }

  public static async listOpalTools(
    trackerId: string,
    instanceId: string,
    installationId: number,
    region: string,
    optiidToken: string
  ): Promise<ListOpalToolResponse> {
    const params = new URLSearchParams({
      'installation_id': installationId.toString(),
      'region': region
    });

    const queryString = params.toString();
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const url = `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/opal/${seg(instanceId)}/tools?${queryString}`;
    const response = await this.performGet(url, headers);
    const jsonResponse = await response.json() as ListOpalToolResponse;
    if (!jsonResponse.tools) {
      jsonResponse.tools = [];
    }

    return jsonResponse;
  }

  public static async registerOpalTool(
    trackerId: string,
    instanceId: string,
    toolName: string,
    installationId: number,
    region: string,
    optiidToken: string
  ): Promise<RegisterOpalToolResponse> {
    const requestBody = {
      tracker_id: trackerId,
      instance_id: instanceId,
      tool_name: toolName,
      installation_id: installationId,
      region
    };

    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const response = await this.performPost(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/opal/${seg(instanceId)}/tools`,
      JSON.stringify(requestBody),
      headers
    );
    return await response.json() as RegisterOpalToolResponse;
  }

  public static async unregisterOpalTool(
    trackerId: string,
    instanceId: string,
    toolId: string,
    region: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const queryString = new URLSearchParams({region}).toString();
    const response = await this.performRequest(
      'DELETE',
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/opal/${seg(instanceId)}/tools/${seg(toolId)}?${queryString}`,
      undefined,
      headers
    );
    return await response.json();
  }

  public static async syncOpalTool(
    trackerId: string,
    instanceId: string,
    toolId: string,
    region: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };

    const queryString = new URLSearchParams({region}).toString();
    await this.performPost(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/opal/${seg(instanceId)}/tools/${seg(toolId)}/sync?${queryString}`,
      JSON.stringify({}),
      headers
    );
  }

  public static async getOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string
  ): Promise<OpalToolDescriptionsResponse> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/app/${seg(appId)}/function/${seg(functionName)}/opal-tools-descriptions`
    );
    return await response.json() as OpalToolDescriptionsResponse;
  }

  public static async updateOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string,
    descriptions: UpdateOpalToolDescriptionsRequest,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };
    await this.performRequest(
      'PATCH',
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/app/${seg(appId)}/function/${seg(functionName)}/opal-tools-descriptions`,
      JSON.stringify(descriptions),
      headers
    );
  }

  public static async deleteOpalToolDescriptions(
    trackerId: string,
    appId: string,
    functionName: string,
    optiidToken: string
  ): Promise<void> {
    const headers = {
      'X-OptiID-Token': optiidToken
    };
    await this.performRequest(
      'DELETE',
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/app/${seg(appId)}/function/${seg(functionName)}/opal-tools-descriptions`,
      undefined,
      headers
    );
  }

  public static async listODPSchemas(trackerId: string): Promise<ObjectDefinition[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/odp/schemas?${new URLSearchParams({tracker_id: trackerId})}`);
    const body =  await response.json() as EnvelopedResponse<'schemas', ObjectDefinition[]>;
    if (body.schemas) {
      return body.schemas;
    } else {
      return [];
    }
  }

  public static async getCGSourceMetadata(
    trackerId: string,
    instanceId: string,
    sourceId?: string
  ): Promise<CGSourceMetadata> {
    // Omitting source_id falls back to the legacy `ocp` source on the backend.
    const sourceIdParam = sourceId ? `&source_id=${encodeURIComponent(sourceId)}` : '';
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/cg/source-metadata?tracker_id=${trackerId}&instance_id=${instanceId}${sourceIdParam}`
    );
    const body = await response.json();
    return {locales: sourceMetadataLocales(body)};
  }

  public static async updateCGSourceMetadata(
    trackerId: string,
    instanceId: string,
    languages: [CGLanguage],
    sourceId?: string
  ): Promise<string> {
    const response = await this.performPatch(
      `${ZIP_API_URI}/v1/cg/source-metadata`,
      JSON.stringify({
        'trackerId': trackerId,
        'instanceId': instanceId,
        'source_id': sourceId || '',
        'additional_locales': languages
      })
    );
    return await response.json();
  }

  public static async listCGBaseTypes(trackerId: string, instanceId: string): Promise<CGBaseTypeDefinition[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/list-base-contracts?${new URLSearchParams({tracker_id: trackerId, instance_id: instanceId})}`);
    const body =  await response.json() as EnvelopedResponse<'base_contracts', CGBaseTypeDefinition[]>;
    if (body.base_contracts) {
      return body.base_contracts;
    } else {
      return [];
    }
  }

  public static async listDestinationSchemas(
    trackerId: string,
    product: string,
    instanceId: string,
    opts?: DestinationSchemaQueryOpts
  ): Promise<ListDestinationSchemasResponse> {
    const query = buildDestinationSchemaQuery(opts);
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/${seg(product)}/${seg(instanceId)}/destinations/schemas${query}`
    );
    return await response.json() as ListDestinationSchemasResponse;
  }

  public static async listSourceSchemas(
    trackerId: string,
    product: string,
    instanceId: string
  ): Promise<ListSourceSchemasResponse> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/${seg(product)}/${seg(instanceId)}/sources/schemas`
    );
    return await response.json() as ListSourceSchemasResponse;
  }

  public static async getSourceSchema(
    trackerId: string,
    product: string,
    instanceId: string,
    schemaId: string
  ): Promise<SourceSchema> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/${seg(product)}/${seg(instanceId)}/sources/schemas/${seg(schemaId)}`
    );
    const body = await response.json() as GetSourceSchemaResponse;
    return body.schema;
  }

  public static async getDestinationSchema(
    trackerId: string,
    product: string,
    instanceId: string,
    schemaId: string,
    opts?: DestinationSchemaQueryOpts
  ) {
    const query = buildDestinationSchemaQuery(opts);
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/${seg(product)}/${seg(instanceId)}/destinations/schemas/${seg(schemaId)}${query}`
    );
    const body = await response.json() as GetCGDestinationSchemaResponse;
    return body.schema;
  }

  public static async saveCGSchema(createCGSchemaRequest: CreateCGSchemaRequest): Promise<void> {
    return await this.performPost(`${ZIP_API_URI}/v1/cg/schema`, JSON.stringify(createCGSchemaRequest));
  }

  public static async getCGInstance(trackerId: string, instanceId: string): Promise<CGProductInstance> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/cg/instance?${new URLSearchParams({tracker_id: trackerId, instance_id: instanceId})}`);
    return await response.json() as CGProductInstance;
  }

  public static async checkCGTypeExists(
    trackerId: string,
    instanceId: string,
    typeName: string
  ): Promise<{exists: boolean; message: string}> {
    const response = await this.performGet(
      `${ZIP_API_URI}/v1/cg/instances/${seg(instanceId)}/types/${seg(typeName)}/exists?${new URLSearchParams({tracker_id: trackerId})}`
    );
    return await response.json();
  }

  public static async listAppSources(trackerId: string, appId: string): Promise<ListSourcesResponse[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/apps/${seg(appId)}/sources`);
    const body =  await response.json() as EnvelopedResponse<'sources', ListSourcesResponse[]>;
    if (body.sources) {
      return body.sources;
    } else {
      return [];
    }
  }

  public static async listAppDestinations(trackerId: string, appId: string): Promise<string[]> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/accounts/${seg(trackerId)}/apps/${seg(appId)}/destinations`);
    const body =  await response.json() as EnvelopedResponse<'destinations', string[]>;
    if (body.destinations) {
      return body.destinations;
    } else {
      return [];
    }
  }

  public static async listHubAppSourceSchema(trackerId: string,
                                             appId: string,
                                             source: string): Promise<ObjectDefinition> {
    const response = await this.performGet(`${ZIP_API_URI}/v2/accounts/${seg(trackerId)}/apps/${seg(appId)}/sources/${seg(source)}/schema`);
    const body = await response.json() as GetSchemaResponse;

    // Convert v2 format to ObjectDefinition format with nested field support
    return convertSchemaV2ToObjectDefinition(body.schema);
  }

  public static async listHubAppDestinationSchema(trackerId: string,
                                                  appId: string,
                                                  destination: string): Promise<ObjectDefinition> {
    const response = await this.performGet(`${ZIP_API_URI}/v2/accounts/${seg(trackerId)}/apps/${seg(appId)}/destinations/${seg(destination)}/schema`);
    const body = await response.json() as GetSchemaResponse;

    // Convert v2 format to ObjectDefinition format with nested field support
    return convertSchemaV2ToObjectDefinition(body.schema);
  }

  public static async fetchFormData(installId: number): Promise<FetchFormDataResponse> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${seg(installId)}/settings`);
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json)
    };
  }

  public static async submitFormData(installId: number, jsonPayload: string): Promise<SubmitFormDataResponse> {
    const response = await this.performPost(`${ZIP_API_URI}/v1/installs/${seg(installId)}/settings`, jsonPayload);
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json),
      response_json: JSON.parse(data.response_json)
    };
  }

  public static async listFunctions(installId: number): Promise<ListFunctionsResponse['function_endpoints']> {
    const response = await this.performGet(`${ZIP_API_URI}/v1/installs/${seg(installId)}/functions`);
    return (await response.json() as ListFunctionsResponse).function_endpoints;
  }

  public static async listAppLogEntries(
    jsonPayload: string,
    attemptsLeft: number = 3
  ): Promise<ListAppLogEntriesResponse> {
    try {
      const response = await this.performPost(`${ZIP_API_URI}/v1/logging/search`, jsonPayload);
      return await response.json();
    } catch (e) {
      if (e instanceof ZipApiError && attemptsLeft > 0) {
        await new Promise( (resolve) => setTimeout(resolve, 100));
        return this.listAppLogEntries(jsonPayload, attemptsLeft - 1);
      }
      if (e instanceof ZipApiTimeoutError) {
        showToasts([
          {
            intent: Intent.DANGER,
            message: 'Timeout while fetching logs. Please try again. Consider narrowing down the date range.'
          }
          ]);
        return {
            log_entry: [],
            cursor_token: ''
        };
      }

      throw e;
    }
  }

  public static async authorizationRequest(installId: number, jsonPayload: string):
    Promise<AuthorizationRequestResponse> {
    const response = await this.performPost(
      `${ZIP_API_URI}/v1/installs/${seg(installId)}/authorization/request`, jsonPayload
    );
    const data = await response.json();
    return {
      form_data_json: JSON.parse(data.form_data_json),
      response_json: JSON.parse(data.response_json),
      auth_request_id: data.auth_request_id
    };
  }

  public static async triggerSync(dataSyncId: string, jobName?: string): Promise<boolean> {
    const requestBody: any = {};
    if (jobName) {
        requestBody.job_name = jobName;
    }
    const jsonRequestBody = JSON.stringify(requestBody);
    const response = await this.performPatch(`${ZIP_API_URI}/v1/data-syncs/${seg(dataSyncId)}/trigger`, jsonRequestBody);
    return response.ok;
  }

  private static async performGet(url: string, headers?: Record<string, string>) {
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        ...headers,
        ...AUTH_HEADERS,
        ...PR_LANE_HEADERS
      }
    });
    logPrLaneResponseHeaders(response);
    if (!response.ok) {
      throw new ZipApiError(response);
    }
    return response;
  }

  private static async performPost(url: string, body: string, headers?: Record<string, string>) {
    return this.performRequest('POST', url, body, headers);
  }

  private static async performPatch(url: string, body?: string, headers?: Record<string, string>) {
    return this.performRequest('PATCH', url, body, headers);
  }

  private static async performRequest(method: string, url: string, body?: string, headers?: Record<string, string>) {
    let response;
    try {
      response = await fetch(url, {
        method,
        body,
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          ...AUTH_HEADERS,
          ...PR_LANE_HEADERS,
        },
        credentials: 'include'
      });
    } catch (e) {
      if (e instanceof TypeError) {
        throw new ZipApiTimeoutError();
      }

      throw e;
    }

    logPrLaneResponseHeaders(response);
    if (!response.ok) {
      throw new ZipApiError(response);
    }
    return response;
  }
}
